import React, { useState } from "react";
import SaveButton from "../../../components/Buttons/SaveButton";
import CheckboxField from "../../../components/Fields/CheckboxField";
import DateControl from "../../../components/Fields/DateControl";
import Field from "../../../components/Fields/Field";
import FieldLabel from "../../../components/Fields/FieldLabel";
import NumberField from "../../../components/Fields/NumberField";
import SelectListField from "../../../components/Fields/SelectListField";
import TextareaField from "../../../components/Fields/TextareaField";
import TextboxField from "../../../components/Fields/TextboxField";
import { FormConfig } from "../../../components/Fields/Types";
import useSelectListOptions from "../../../components/Hooks/SelectListOptionsHook/useSelectListOptions";
import useValidation from "../../../components/Hooks/ValidationHook/useValidation";
import Popup from "../../../components/Popups/Popup";
import { useNotifications } from "../../../contexts/notificationContext";
import DataService from "../../../services/data.service";
import { PopupProps } from "../../../types/PopupProps";
import { DataResult } from "../../../types/Result";
import FormatUtils from "../../../utils/FormatUtils";

interface Props extends PopupProps {
    workflowDefinitionId,
    workflowStepDefinitionId
}

interface WorkflowActionCreateModel {
    workflowStepDefinitionId: number
    name: string
    workflowTaskDefinitionId?: number
    nextWorkflowStepDefinitionId: number
    buttonOrder: number
    beginDate: string
    endDate?: string
}

const AddWorkflowStep: React.FC<Props> = ({ show, onHide, workflowDefinitionId, workflowStepDefinitionId }) => {
    const workflowTaskDefinitions = useSelectListOptions('api/v1/selectlist/WorkflowTaskDefinitions?workflowStepDefinitionId=' + workflowStepDefinitionId ?? 0, '-- All tasks');
    const workflowStepDefinitions = useSelectListOptions('api/v1/selectlist/WorkflowStepDefinitions?workflowDefinitionId=' + workflowDefinitionId, '-- Select step');
    const [serverErrors, setServerErrors] = useState<DataResult<WorkflowActionCreateModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException } = useNotifications();

    const [formData, setFormData] = useState<WorkflowActionCreateModel>({
        workflowStepDefinitionId: workflowStepDefinitionId,
        name: '',
        workflowTaskDefinitionId: 0,
        nextWorkflowStepDefinitionId: 0,
        buttonOrder: 1,
        beginDate: new FormatUtils().formatDateForStorage(new Date()),
        endDate: '',
    });

    function submit() {
        if (formData) {
            DataService
                .create<WorkflowActionCreateModel>(`api/v1/WorkflowActions/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        onHide()
                })
                .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'name', label: 'Button text', validationRules: [{ type: 'isRequired' }] },
            { name: 'workflowTaskDefinitionId', label: 'Task' },
            { name: 'nextWorkflowStepDefinitionId', label: 'Next step', validationRules: [{ type: 'isRequired' }] },
            { name: 'buttonOrder', label: 'Display order', validationRules: [{ type: 'isRequired' }] },
            { name: 'beginDate', label: 'Active from', labelSize: 3, validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
            { name: 'endDate', label: 'Active until', labelSize: 3, validationRules: [{ type: 'isDate' }] },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    return (
        <Popup show={show} onHide={onHide} size={'lg'} title={'Add action'} >
            <Popup.Body>
                <TextboxField {...getFieldProps('name')} />
                <SelectListField {...getFieldProps('workflowTaskDefinitionId')} options={workflowTaskDefinitions} />
                <SelectListField {...getFieldProps('nextWorkflowStepDefinitionId')} options={workflowStepDefinitions} />
                <NumberField {...getFieldProps('buttonOrder')} />
                <Field {...getLabelProps('beginDate')}>
                    <DateControl {...getControlProps('beginDate')} />
                    <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                    <DateControl {...getControlProps('endDate')} />
                </Field>
            </Popup.Body>
            <Popup.Footer>
                <SaveButton {...getSubmitButtonProps()} text='Add' />
            </Popup.Footer>
        </Popup>
    );
}
export default AddWorkflowStep;